import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, Paper, Select, MenuItem, IconButton, Collapse, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { supabase } from '../supabaseClient';

const predefinedAgents = [
  { name: 'Hearthstone Card Designer', prompt: '# 《炉石传说》卡牌设计师\n\n你来充当一位《炉石传说》卡牌设计师。\n\n## 任务\n\n我用自然语言告诉你要生成的《炉石传说》卡牌的相关信息，你的任务是根据这个主题设计一个完整的《炉石传说》卡牌描述，包含了卡牌各部分的位置、画面、文字、数值等信息。\n\n## 背景介绍\n\n《炉石传说》是一款由暴雪娱乐开发的数字卡牌游戏。玩家通过构建卡组与对手进行对战，使用各种卡牌来施放法术、召唤随从和发动技能。每张卡牌都包含丰富的信息和设计元素，以下是卡牌的主要组成部分和常见关键词：\n\n## 卡牌组成部分\n\nCard Name: Located at the top of the card, indicating the cards name.\nMana Cost: Located at the top left of the card, indicating the mana required to use the card.\nAttack: Located at the bottom left of the card, indicating the attack power of a minion or weapon. Spell cards do not have this.\nHealth/Durability: Located at the bottom right of the card, indicating the health of a minion or the durability of a weapon. Spell cards do not have this.\nCard Type: Includes minion, spell, weapon, etc., usually located in the upper middle part of the card.\nMinion Attributes: Minion cards display attack and health, located at the bottom left and bottom right of the card, respectively.\nCard Effect Description: Located in the middle of the card, detailing the specific effect of the card.\nCard Artwork: Located in the center of the card, showcasing the cards artistic design.\nrace: Located at the bottom of the card, Some cards indicate their race (e.g., Dragon, Mech, Demon, Elemental, Murloc, Pirate, Beast, Totem, Undead, Demon Hunter) \noccupation: (e.g., Mage, Warrior, Priest, Rogue, Shaman, Warlock, Hunter, Paladin, Druid, Demon Hunter).\nRarity Gem and Corresponding Color: Legendary (orange), Epic (purple), Rare (blue), Common (white).\nCommon keywords in card effect descriptions:\n\nBattlecry: An effect that triggers when you play the minion.\nDeathrattle: An effect that triggers when the minion dies.\nCharge: The minion can attack immediately.\nTaunt: Enemies must attack minions with Taunt first.\nSpell Damage: Increases the damage dealt by your spells.\nWindfury: The minion can attack twice each turn.\nStealth: The minion cannot be targeted until it attacks.\nLifesteal: Damage dealt by the minion heals your hero for the same amount.\nPoisonous: Any minion damaged by this minion is destroyed, regardless of its health.\nMagnetic: A keyword unique to Mech minions, allowing them to combine with other Mech minions, stacking their attributes and effects.\nReborn: The minion returns to life with 1 health the first time it dies.\nOverkill: An effect that triggers when the minion deals damage exceeding the targets health.\nSpellburst: An effect that triggers when you cast a spell.\nCombo: An additional effect if you have played another card this turn.\nInspire: An effect that triggers when you use your Hero Power.\nDiscover: Choose one of three random cards to add to your hand.\n\n## 生成方式\n{}中是你要生成填充的部分，对于我没有给出的信息，你需要自行丰富联想，然后设计出来。以下是你要输出的结构：\n\nThis picture is a digital illustration of Hearthstone cards (especially card games). It depicts {image in card}.\nThe text "{Card Name}" on the card is written in stylized fantasy font, and the text is located in the center of the card. In the middle is written "{Card Effect Description}" Below the text, there is a huge rectangular beige banner with a gold border. Below the banner is a glowing {rare gem color} small gem. There is a number in each of the upper left, lower left, and lower right corners of the card, and each number font color is white. The number "{mana value}" in the upper left corner is contained in a hexagonal blue border. The number "{attack power}" is located in the lower left corner, above the logo of a sword, and the number "{health point/durability value}" is located in the lower right corner, above the logo of a drop of blood. In the middle of the bottom two numbers is the text "{race}" instead of the gem pattern.\n\n## 示例\n\nThis image is a digital illustration of Hearthstone cards (especially card games). It depicts a magma element holding explosives in one hand.\nThe text on the card, "Blaster Rager," is written in stylized fantasy font, with the text located in the center of the card. The middle reads, "Battlecry: Appears on the opponent\'s field. Deathrattle: Deals 8 damage to your hero." Below the text, there is a large rectangular beige banner, and below the banner contains a small glowing orange gem. There is a number each in the upper left, lower left, and lower right corners of the card, and each number font color is white. The number "3" in the upper left corner is contained in a hexagonal blue border. The number "5" is located in the lower left corner, above the logo of a sword, and the number "1" is located in the lower right corner, above the logo of a drop of blood. In the middle of the bottom two numbers is the text "Elemental" instead of the gem pattern.\n\n## 限制\n你必须使用英文来描述这个卡牌。\n\n接下来，请你根据我的输入，丰富细节，设计卡牌中所有的相关内容。\n\n开始！' },
  { name: 'Creative Writer', prompt: 'You are a creative writer who specializes in generating imaginative and vivid descriptions for image generation.' },
  { name: 'Sci-Fi Artist', prompt: 'You are a sci-fi artist who creates futuristic and otherworldly concepts for image generation.' },
  { name: 'Fantasy Illustrator', prompt: 'You are a fantasy illustrator who brings magical and mythical scenes to life through detailed descriptions.' },
  { name: 'Portrait Artist', prompt: 'You are a portrait artist who excels at describing unique and expressive character features for image generation.' },
  { name: 'Landscape Designer', prompt: 'You are a landscape designer who creates breathtaking natural and urban environments through descriptive language.' },
  { name: 'Abstract Conceptualizer', prompt: 'You are an abstract artist who translates emotions and concepts into visual descriptions for unique image generation.' },
];

const AgentMemory = ({ session, currentAgent, setCurrentAgent }) => {
  const [customAgents, setCustomAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [customAgentName, setCustomAgentName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (session) {
      fetchCustomAgents();
    }
  }, [session]);

  const fetchCustomAgents = async () => {
    const { data, error } = await supabase
      .from('custom_agents')
      .select('*')
      .eq('user_id', session.user.id);

    if (error) {
      console.error('Error fetching custom agents:', error);
    } else {
      setCustomAgents(data);
    }
  };

  const handleAgentChange = (event) => {
    const agentName = event.target.value;
    setSelectedAgent(agentName);
    const agent = [...predefinedAgents, ...customAgents].find(a => a.name === agentName);
    setCurrentAgent(agent);
    setCustomPrompt(agent ? agent.prompt : '');
    setCustomAgentName(agent ? agent.name : '');
    setIsEditing(false);
  };

  const handleCustomPromptChange = (event) => {
    const newPrompt = event.target.value;
    setCustomPrompt(newPrompt);
    if (selectedAgent) {
      const updatedAgent = { ...currentAgent, prompt: newPrompt };
      setCurrentAgent(updatedAgent);
    }
  };

  const [successMessage, setSuccessMessage] = useState('');

  const handleSaveCustomAgent = async () => {
    if (!customPrompt.trim() || !customAgentName.trim() || !session) return;

    if (selectedAgent && customAgents.some(a => a.name === selectedAgent)) {
      // Update existing custom agent
      const agentToUpdate = customAgents.find(a => a.name === selectedAgent);
      const { error } = await supabase
        .from('custom_agents')
        .update({ name: customAgentName, prompt: customPrompt })
        .eq('id', agentToUpdate.id);

      if (error) {
        console.error('Error updating custom agent:', error);
      } else {
        const updatedAgents = customAgents.map(a => 
          a.name === selectedAgent ? { ...a, name: customAgentName, prompt: customPrompt } : a
        );
        setCustomAgents(updatedAgents);
        setCurrentAgent({ ...currentAgent, name: customAgentName, prompt: customPrompt });
        setSelectedAgent(customAgentName);
        setSuccessMessage('Agent updated successfully!');
      }
    } else {
      // Create new custom agent
      const newAgent = {
        name: customAgentName,
        prompt: customPrompt,
        user_id: session.user.id,
      };

      const { error } = await supabase
        .from('custom_agents')
        .insert([newAgent]);

      if (error) {
        console.error('Error saving custom agent:', error);
      } else {
        setCustomAgents([...customAgents, newAgent]);
        setSelectedAgent(newAgent.name);
        setCurrentAgent(newAgent);
        setSuccessMessage('New agent created successfully!');
      }
    }
    setIsEditing(false);
    fetchCustomAgents(); // Refresh the dropdown menu
    setTimeout(() => setSuccessMessage(''), 3000); // Clear success message after 3 seconds
  };

  const handleDeleteCustomAgent = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDeleteCustomAgent = async () => {
    if (!selectedAgent || !session) {
      setDeleteDialogOpen(false);
      return;
    }

    const agentToDelete = customAgents.find(a => a.name === selectedAgent);
    if (!agentToDelete) {
      setDeleteDialogOpen(false);
      return;
    }

    const { error } = await supabase
      .from('custom_agents')
      .delete()
      .eq('id', agentToDelete.id);

    if (error) {
      console.error('Error deleting custom agent:', error);
    } else {
      setCustomAgents(customAgents.filter(a => a.name !== selectedAgent));
      setSelectedAgent('');
      setCurrentAgent(null);
      setCustomPrompt('');
      setSuccessMessage('Agent deleted successfully!');
    }
    setDeleteDialogOpen(false);
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
  };

  return (
    <Paper elevation={3} sx={{ p: 2, mb: 2, background: 'rgba(60,60,60,0.8)', borderRadius: '10px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <Typography variant="h6">
          {!expanded && selectedAgent ? selectedAgent : "Agent Memory"}
        </Typography>
        <IconButton>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      {successMessage && (
        <Typography 
          variant="body2" 
          sx={{ 
            mt: 1, 
            p: 1, 
            bgcolor: 'success.main', 
            color: 'success.contrastText', 
            borderRadius: 1 
          }}
        >
          {successMessage}
        </Typography>
      )}
      <Collapse in={expanded}>
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Select
              value={selectedAgent}
              onChange={handleAgentChange}
              displayEmpty
              fullWidth
              sx={{ mr: 1 }}
            >
              <MenuItem value="" disabled>Select an Agent</MenuItem>
              {predefinedAgents.map((agent) => (
                <MenuItem key={agent.name} value={agent.name}>{agent.name}</MenuItem>
              ))}
              {customAgents.map((agent) => (
                <MenuItem key={agent.name} value={agent.name}>{agent.name}</MenuItem>
              ))}
            </Select>
            <IconButton onClick={() => setIsEditing(true)}>
              <AddIcon />
            </IconButton>
            {selectedAgent && customAgents.some(a => a.name === selectedAgent) && (
              <IconButton onClick={handleDeleteCustomAgent}>
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Agent's name..."
            value={customAgentName}
            onChange={(e) => setCustomAgentName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder="Agent's system prompt..."
            value={customPrompt}
            onChange={handleCustomPromptChange}
          />
          <Button onClick={handleSaveCustomAgent} sx={{ mt: 1 }}>
            {selectedAgent && customAgents.some(a => a.name === selectedAgent) ? 'Update Agent' : 'Save Agent'}
          </Button>
        </Box>
      </Collapse>
      {/* Diálogo de confirmación para eliminar agente */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-agent-dialog-title"
        aria-describedby="delete-agent-dialog-description"
      >
        <DialogTitle id="delete-agent-dialog-title">Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-agent-dialog-description">
            ¿Estás seguro de que deseas eliminar el agente "{selectedAgent}"? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="primary">
            Cancelar
          </Button>
          <Button onClick={confirmDeleteCustomAgent} color="secondary" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AgentMemory;
